<template>
    <div class="email-onboarding-mode">
        <!--  Synchronisé -->
        <template v-for="{ id, title, ...card } in cards">
            <div v-if="card.active" :key="card.id" class="mode-card">
                <div v-if="isRecommandedMode(id)" class="mode-card__tag">
                    <span class="mode-card__tag-label typographies-paragraph-small-semibold">
                        {{ $l10n.__('Recommandé') }}
                    </span>
                </div>
                <div class="mode-card__container">
                    <div class="mode-card__header">
                        <h3 class="mode-card__header-title typographies-heading-h3">{{ title }}</h3>
                        <p class="mode-card__header-subtitle typographies-paragraph-medium-regular">
                            {{ card.subtitle }}
                        </p>
                    </div>
                    <div class="mode-card__content">
                        <div
                            v-for="({ icon, title, description }, index) in card.features"
                            :key="index"
                            class="mode-card__content-feature"
                        >
                            <i :class="icon"></i>
                            <div class="mode-card__content-feature-informations">
                                <h3
                                    class="mode-card__content-feature-informations-title typographies-paragraph-medium-bold"
                                >
                                    {{ title }}
                                </h3>
                                <p class="mode-card__content-feature-informations-description">
                                    {{ description }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="mode-card__actions">
                        <CompassLink
                            :label="card.action.moreDetails.label"
                            :link="card.action.moreDetails.href"
                            target="_blank"
                        />
                        <CompassButton
                            :disabled="isDisabledMode(id)"
                            :label="card.action.select.label"
                            @click="$emit('validMode', { id, title })"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import Feature from 'Services/Feature';

export default {
    name: 'EmailOnboardingMode',
    props: {
        provider: {
            type: Object,
            required: false,
            default: null,
        },
        useImap: {
            type: String,
            required: false,
            default: 'N',
        },
    },
    data() {
        return {
            deleteModal: false,
        };
    },
    computed: {
        isNylasApiV3Enabled() {
            return Feature.isEnabled('email-engine') && Feature.isEnabled('nylas-api-v3');
        },
        isNylasApiV2Blocked() {
            if (this.isNylasApiV3Enabled) {
                return true;
            }

            return !Feature.isEnabled('nylas-api-v2');
        },
        isSyncModeEnabled() {
            if (this.isNylasApiV3Enabled) {
                return true;
            }

            return !this.isNylasApiV2Blocked;
        },
        cards() {
            return [
                {
                    id: 'sync',
                    title: this.$l10n.__('Synchronisé'),
                    subtitle: this.$l10n.__(
                        'Toute la puissance de votre compte email synchronisé avec Sellsy',
                    ),
                    features: [
                        {
                            icon: 'icon-customfield',
                            title: this.$l10n.__('Intégration complète'),
                            description: this.$l10n.__(
                                'Vos emails seront synchronisés avec Sellsy, ainsi partout où vous serez et avec une simple connexion internet, vous y aurez accès.',
                            ),
                        },
                        {
                            icon: 'icon-activity',
                            title: this.$l10n.__('Suivi des emails'),
                            description: this.$l10n.__(
                                'Assurez vous que votre destinataire à bien recu le message : Suivez l’envoi, la réception, l’ouverture de vos emails envoyés directement depuis Sellsy.',
                            ),
                        },
                        {
                            icon: 'icon-refresh',
                            title: this.$l10n.__('Synchronisation totale'),
                            description: this.$l10n.__(
                                'Déplacez, envoyez, marquez,… Un email depuis Sellsy et ces actions sont visibles sur tous les appareils qui ont synchronisés votre compte.',
                            ),
                        },
                    ],
                    action: {
                        moreDetails: {
                            label: this.$l10n.__('En savoir plus'),
                            href: 'https://help.sellsy.com/hc/receiving-and-sending-emails-with-sellsy/gestion-des-emails-en-mode-synchronisation',
                        },
                        select: {
                            label: this.$l10n.__('Sélectionner le mode synchronisé'),
                        },
                    },
                    active: this.isSyncModeEnabled,
                },
                {
                    id: 'transfert',
                    title: this.$l10n.__('Transfert'),
                    subtitle: this.$l10n.__(
                        'La méthode simple pour envoyer et suivre des emails à vos contacts.',
                    ),
                    features: [
                        {
                            icon: 'icon-export',
                            title: this.$l10n.__('Transfert'),
                            description: this.$l10n.__(
                                'Un service simple : envoyez des emails et documents Sellsy depuis votre interface. Seuls les emails envoyés depuis Sellsy seront visibles.',
                            ),
                        },
                        {
                            icon: 'icon-activity',
                            title: this.$l10n.__('Suivi des emails'),
                            description: this.$l10n.__(
                                'Assurez vous que votre destinataire a bien reçu le message : Suivez l’envoi, la réception, l’ouverture de vos emails envoyés depuis Sellsy.',
                            ),
                        },
                        {
                            icon: 'icon-setting',
                            title: this.$l10n.__('Authentification du domaine'),
                            description: this.$l10n.__(
                                'Pour une meilleure délivrabilité de vos emails dans ce mode, vous pouvez désormais authentifier votre domaine.',
                            ),
                        },
                    ],
                    action: {
                        moreDetails: {
                            label: this.$l10n.__('En savoir plus'),
                            href: 'https://help.sellsy.com/hc/receiving-and-sending-emails-with-sellsy/gestion-des-emails-en-mode-transfert',
                        },
                        select: {
                            label: this.$l10n.__('Sélectionner le mode transfert'),
                        },
                    },
                    active: true,
                },
            ];
        },
    },
    methods: {
        /**
         * Return true if is recommanted mode for selected providers
         *  - sync recommended for gmail and office365
         *  - transfert recommended for other
         *
         * @param {string} cardId - id of current card
         * @return {boolean}
         */
        isRecommandedMode(cardId) {
            return cardId === 'sync'
                ? this.provider && this.provider.id !== 'other'
                : this.provider && this.provider.id === 'other';
        },

        /**
         * Return true if user can not select mode ()
         *  - sync disabled if provider "other" is selected && useImap is desactived
         *  - transfert never disabled
         *
         * @param {string} cardId - id of current card
         * @return {boolean}
         */
        isDisabledMode(cardId) {
            return cardId === 'sync'
                ? this.provider && this.provider.id === 'other' && this.useImap === 'N'
                : false;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.collapse-onboarding-mail .email-onboarding-mode {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    gap: var(--spacing-1);
    width: 80%;
    margin: 0 auto;

    .mode-card {
        max-width: 50%;
        position: relative;
        padding: var(--spacing-4) var(--spacing-5) var(--spacing-5);
        border-radius: 4px;
        border: 1px solid var(--colors-common-border-default);
        background-color: var(--colors-common-background-default);

        &__tag {
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -50%);
            padding: var(--spacing-0) var(--spacing-1);
            border-radius: 12px;
            background-color: var(--colors-sellsy-extended-tag-light-purple);

            &-label {
                color: var(--colors-common-text-standard-on-color);
                text-transform: uppercase;
            }
        }

        &__container {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: var(--spacing-3);
            height: 100%;
        }

        &__header {
            display: flex;
            flex-direction: column;
            align-items: center;
            border-bottom: 1px solid var(--colors-common-border-default);

            &-title {
                margin: 0;
            }

            &-subtitle {
                margin: 0;
                padding: var(--spacing-3) 0;
                color: var(--colors-common-text-standard-caption);
                text-align: center;
            }
        }

        &__content {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-4);

            &-feature {
                display: flex;
                align-items: flex-start;
                gap: var(--spacing-3);

                &-informations {
                    &-title {
                        margin: 0;
                    }

                    &-description {
                        margin: 0;
                        color: var(--colors-common-text-standard-caption);
                    }
                }
            }
        }

        &__actions {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--spacing-3);
        }
    }
}
</style>
